import { styled } from '@uhg-abyss/web/tools/styled';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { RatingText } from '../../../../../common/DataCard/DataCard.style';
import { PrintViewDivider } from './PrintView.style';
import {
  getAddress,
  getCostIndicatorDescription,
  getHoursOfOperation,
  getObjectStringParsed,
  getPhoneNumbers,
} from './printViewUtils';
import { getStarsRating } from './utils';

const baseBoldTextStyle = {
  fontWeight: '700',
  color: '#212223',
};

export const PrintViewBoldText = styled('span', {
  ...baseBoldTextStyle,
});

export const PrintViewBoldTextWithMarginRight = styled('span', {
  ...baseBoldTextStyle,
  marginRight: '2px',
});

const baseFlexRootCss = {
  gap: '2px',
  padding: 0,
  flexWrap: 'nowrap',
  width: '100%',
  fontSize: '10px',
} as const;

export const PrintViewSection = styled(Flex, {
  '&.abyss-flex-root': baseFlexRootCss,
  variants: {
    spacing: {
      top: {
        '&.abyss-flex-root': { marginTop: '10px' },
      },
      vertical: {
        '&.abyss-flex-root': { marginTop: '20px', marginBottom: '20px' },
      },
    },
  },
});

export const PrintViewInlineItem = styled(Flex, {
  '&.abyss-flex-root': {
    marginRight: '5px',
    gap: '4px',
  },
  variants: {
    spacing: {
      wide: {
        '&.abyss-flex-root': { marginRight: '8px' },
      },
    },
  },
});

const PrintViewIcon = ({ icon }) => (
  <IconSymbol
    color="#111"
    css={{
      marginTop: '1px',
    }}
    icon={icon}
    size="xs"
  />
);

export const PrintViewRow = ({ children }) => (
  <PrintViewSection direction="row">{children}</PrintViewSection>
);

const BehavioralHealthProviderPrintView = ({ portalData, provider }) => {
  const { t } = useTranslation();
  const notAvailable = t('PROVIDER_LOCATIONS_DETAILS.NOT_AVAILABLE_UNKNOWN');

  const acceptingNewPatients = provider?.acceptingNewPatients;
  const accessibilities = provider?.accessibility?.join(', ') || notAvailable;
  const additionalAddresses = [];
  const address = getAddress(provider?.address) || notAvailable;
  const boardCertifications =
    provider?.boardCertifications?.join(', ') || notAvailable;
  const constIndicator = getCostIndicatorDescription(
    provider?.costIndicator,
    t
  );
  const culturalCompetencies =
    provider?.culturalCompetencies?.join(', ') || notAvailable;
  const afterHoursPhone =
    getPhoneNumbers(provider?.phones?.emergency) || notAvailable;
  const email = provider?.emailAddress?.join(', ') || notAvailable;
  const faxPhone = getPhoneNumbers(provider?.phones?.fax) || notAvailable;
  const gender = provider?.gender;
  const hasEveningAppointments =
    provider?.hasEveningAppointments == null
      ? notAvailable
      : provider?.hasEveningAppointments
      ? 'Yes'
      : 'No';
  const hasWeekendAppointments =
    provider?.hasWeekendAppointment == null
      ? notAvailable
      : provider?.hasWeekendAppointment
      ? 'Yes'
      : 'No';
  const hospitalAffiliations =
    getObjectStringParsed(provider?.hospitalAffiliations) || notAvailable;
  const hoursOfOperation = getHoursOfOperation([provider?.hoursOfOperation], t);
  const isPremiumCarePhysician = provider?.isPremiumCarePhysician === 'YES';
  const isTieredProvider = provider?.isTieredProvider;
  const languagesSpoken = provider?.languagesSpoken?.join(', ') || notAvailable;
  const languagesSpokenByInterpreters =
    provider?.languagesSpokenByInterpreters?.join(', ') || notAvailable;
  const languagesSpokenByOffice =
    provider?.languagesSpokenByOffice?.join(', ') || notAvailable;
  const licenseNumber = provider?.licenseNumber?.join(', ') || notAvailable;
  const networkOrPlanName = portalData?.planName || notAvailable;
  const npi = provider?.npi || notAvailable;
  const practiceAgeLimitations =
    provider?.practiceLimitations?.age || notAvailable;
  const practiceGenderLimitations =
    provider?.practiceLimitations?.gender || notAvailable;
  const providerGroupsAffiliations = getObjectStringParsed(
    provider?.providerGroupsAffiliations,
    t
  );
  const providerName = provider?.providerName || notAvailable;

  const rating = provider?.healthGradeRating;
  const parsedProviderRating = parseFloat(parseFloat(rating).toFixed(1));
  const specialties = getObjectStringParsed(provider?.specialities, t);
  const telephone = getPhoneNumbers(provider?.phones?.phone) || notAvailable;
  const tty = provider?.tty || notAvailable;
  const virtualCareOffered =
    provider?.virtualCareOffered?.length > 0 ? 'Yes' : notAvailable;
  const website = provider?.website || notAvailable;

  return (
    <React.Fragment>
      {/* index and name section start */}
      <PrintViewSection direction="column">
        <PrintViewBoldText>
          {providerName} {gender ? `(${gender})` : ''}
        </PrintViewBoldText>
      </PrintViewSection>
      {/* index and name section end */}

      {/* Specialties section start */}
      <PrintViewRow>
        <PrintViewBoldTextWithMarginRight>
          {t('PROVIDER_LOCATIONS_DETAILS.SPECIALTIES')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{specialties}</span>
      </PrintViewRow>
      {/* Specialties section end */}

      {/* rating, accepting new patients, virtual care section end */}
      <PrintViewSection justify="flex-start" spacing="vertical">
        {rating && (
          <PrintViewInlineItem direction="row">
            {getStarsRating(parsedProviderRating)}
            {parsedProviderRating ? (
              <RatingText className="text" fontSize="xs">
                {provider?.numberOfReviews
                  ? `${provider?.numberOfReviews} Reviews`
                  : parsedProviderRating}
              </RatingText>
            ) : null}
          </PrintViewInlineItem>
        )}
        {isPremiumCarePhysician && (
          <PrintViewInlineItem direction="row" spacing="wide">
            <PrintViewBoldText>
              <PrintViewIcon icon="favorite" />
              <PrintViewIcon icon="favorite" />{' '}
              {t('SHARE_PDF.Premium Care Physician')}
            </PrintViewBoldText>
          </PrintViewInlineItem>
        )}
        {isTieredProvider && (
          <PrintViewInlineItem direction="row">
            <PrintViewBoldText>
              <PrintViewIcon icon="check" /> {t('SHARE_PDF.Tier 1 Provider')}
            </PrintViewBoldText>
          </PrintViewInlineItem>
        )}
        {acceptingNewPatients && (
          <PrintViewInlineItem direction="row" spacing="wide">
            <PrintViewIcon icon="check" />{' '}
            <PrintViewBoldText>
              {t('SHARE_PDF.Accepting new patients')}
            </PrintViewBoldText>
          </PrintViewInlineItem>
        )}
        {constIndicator && (
          <PrintViewInlineItem direction="row">
            <PrintViewBoldText>
              {provider?.costIndicator} - {constIndicator}
            </PrintViewBoldText>
          </PrintViewInlineItem>
        )}
      </PrintViewSection>
      {/* rating, accepting new patients, virtual care section end */}

      {/* address section start */}
      <PrintViewRow>
        <PrintViewBoldTextWithMarginRight>
          {t('PROVIDER_LOCATIONS_DETAILS.ADDRESS')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{address}</span>
      </PrintViewRow>
      {/* address section end */}

      {/* primary phone, fax, email section end */}
      <PrintViewSection justify="flex-start">
        <PrintViewInlineItem direction="row">
          <PrintViewBoldTextWithMarginRight>
            {t('PROVIDER_LOCATIONS_DETAILS.PRIMARY_PHONE')}:
          </PrintViewBoldTextWithMarginRight>
          <span>{telephone}</span>
        </PrintViewInlineItem>

        <PrintViewInlineItem direction="row" spacing="wide">
          <PrintViewBoldTextWithMarginRight>
            {t('SHARE_PDF.Fax')}:
          </PrintViewBoldTextWithMarginRight>
          <span>{faxPhone}</span>
        </PrintViewInlineItem>

        <PrintViewInlineItem direction="row">
          <Flex direction="row">
            <PrintViewBoldTextWithMarginRight>
              {t('FEEDBACK_FORM.EMAIL')}:
            </PrintViewBoldTextWithMarginRight>
            <span>{email}</span>
          </Flex>
        </PrintViewInlineItem>
      </PrintViewSection>
      {/* primary phone, fax, email section end */}

      {/* Website section start */}
      <PrintViewRow>
        <PrintViewBoldTextWithMarginRight>
          {t('Website')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{website}</span>
      </PrintViewRow>
      {/* Website section end */}

      {/* Office hours section start */}
      <PrintViewRow>
        <PrintViewBoldTextWithMarginRight>
          {t('SHARE_PDF.Office hours')}:
        </PrintViewBoldTextWithMarginRight>
        <span>
          {hoursOfOperation?.map((item, index) => (
            <Flex direction="row" key={index}>
              <PrintViewBoldTextWithMarginRight>
                {item.day}:
              </PrintViewBoldTextWithMarginRight>
              <span>{item.timing}</span>
            </Flex>
          ))}
        </span>
      </PrintViewRow>
      {/* Office hours section end */}

      {/* Virtual care section start */}
      <PrintViewRow>
        <PrintViewBoldTextWithMarginRight>
          {t('PROVIDER_LOCATIONS_DETAILS.OFFERS_VIRTUAL_CARE')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{virtualCareOffered}</span>
      </PrintViewRow>
      {/* Virtual care section end */}

      {/* After hours section start */}
      <PrintViewRow>
        <PrintViewBoldTextWithMarginRight>
          {t('SHARE_PDF.After hours')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{afterHoursPhone}</span>
      </PrintViewRow>
      {/* After hours section end */}

      {/* TTY section start */}
      <PrintViewRow>
        <PrintViewBoldTextWithMarginRight>
          TTY:
        </PrintViewBoldTextWithMarginRight>
        <span>{tty}</span>
      </PrintViewRow>
      {/* TTY section end */}

      {/* Evening appointments section start */}
      <PrintViewRow>
        <PrintViewBoldTextWithMarginRight>
          {t('SHARE_PDF.Evening appointments')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{hasEveningAppointments}</span>
      </PrintViewRow>
      {/* Evening appointments section end */}

      {/* Weekend appointments section start */}
      <PrintViewRow>
        <PrintViewBoldTextWithMarginRight>
          {t('SHARE_PDF.Weekend appointments')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{hasWeekendAppointments}</span>
      </PrintViewRow>
      {/* Weekend appointments section end */}

      {/* Board certificate section start */}
      <PrintViewSection direction="row" spacing="top">
        <PrintViewBoldTextWithMarginRight>
          {t('SMART_CHOICE.PREFERENCES.Board_certified')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{boardCertifications}</span>
      </PrintViewSection>
      {/* Board certificate section end */}

      {/* Provider group section start */}
      <PrintViewRow>
        <PrintViewBoldTextWithMarginRight>
          {t('Provider group')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{providerGroupsAffiliations}</span>
      </PrintViewRow>
      {/* Provider group section end */}

      {/* hospital Affiliations section start */}
      <PrintViewRow>
        <PrintViewBoldTextWithMarginRight>
          {t(
            'PROVIDER_DETAILS.ABOUT_TAB.DETAILS_SECTION.HOSPITAL_AFFILIATIONS'
          )}
          :
        </PrintViewBoldTextWithMarginRight>
        <span>{hospitalAffiliations}</span>
      </PrintViewRow>
      {/* hospital Affiliations section end */}

      {/* Network and plan name section start */}
      <PrintViewSection direction="row" spacing="top">
        <PrintViewBoldTextWithMarginRight>
          {t('SHARE_PDF.Network/Plan name')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{networkOrPlanName}</span>
      </PrintViewSection>
      {/* Network and plan name section end */}

      {/*License Number and License type section start */}
      <PrintViewSection justify="flex-start">
        <PrintViewInlineItem direction="row">
          <PrintViewBoldTextWithMarginRight>
            {t('SHARE_PDF.License No')}:
          </PrintViewBoldTextWithMarginRight>
          <span>{licenseNumber}</span>
        </PrintViewInlineItem>
      </PrintViewSection>
      {/*License Number and License type section end */}

      {/* NPI section start */}
      <PrintViewRow>
        <PrintViewBoldText>
          {t('SHARE_PDF.National provider No. (NPI)')}:
        </PrintViewBoldText>
        <span>{npi}</span>
      </PrintViewRow>
      {/* NPI section end */}

      {/* accessibility section start */}
      <PrintViewSection direction="row" spacing="top">
        <PrintViewBoldTextWithMarginRight>
          {t('PROVIDER_LOCATIONS_DETAILS.ACCESSIBILITY')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{accessibilities}</span>
      </PrintViewSection>
      {/* accessibility section end */}

      {/* Language spoken by provider section start */}
      <PrintViewRow>
        <PrintViewBoldTextWithMarginRight>
          {t('PROVIDER_LOCATIONS_DETAILS.LANGUAGES_SPOKEN_BY_PROVIDER')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{languagesSpoken}</span>
      </PrintViewRow>
      {/* Language spoken by provider  section end */}

      {/* Language spoken by interpreter section start */}
      <PrintViewRow>
        <PrintViewBoldTextWithMarginRight>
          {t('PROVIDER_LOCATIONS_DETAILS.LANGUAGES_SPOKEN_BY_INTERPRETER')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{languagesSpokenByInterpreters}</span>
      </PrintViewRow>
      {/* Language spoken by interpreter  section end */}

      {/* Language spoken by office section start */}
      <PrintViewRow>
        <PrintViewBoldTextWithMarginRight>
          {t('PROVIDER_LOCATIONS_DETAILS.LANGUAGES_SPOKEN_BY_OFFICE')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{languagesSpokenByOffice}</span>
      </PrintViewRow>
      {/* Language spoken by office  section end */}

      {/* Practice gender section start */}
      <PrintViewRow>
        <PrintViewBoldTextWithMarginRight>
          {t('SHARE_PDF.Practice gender limitations')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{practiceGenderLimitations}</span>
      </PrintViewRow>
      {/* practice gender  section end */}

      {/* practice age section start */}
      <PrintViewRow>
        <PrintViewBoldTextWithMarginRight>
          {t('SHARE_PDF.Practice age limitations')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{practiceAgeLimitations}</span>
      </PrintViewRow>
      {/* practice age  section end */}

      {/* Cultural Competencies section start */}
      <PrintViewRow>
        <PrintViewBoldTextWithMarginRight>
          {t('PROVIDER_GROUP_DETAILS.ABOUT_TAB.CULTURAL_COMPETENCE')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{culturalCompetencies}</span>
      </PrintViewRow>
      {/* Cultural Competencies  section end */}

      {/* Provider locations available in the following location start */}
      {additionalAddresses.map((address, index) => (
        <PrintViewSection direction="row" key={index} spacing="top">
          <PrintViewBoldTextWithMarginRight>
            {t('SHARE_PDF.This provider is also available at')}:
          </PrintViewBoldTextWithMarginRight>
          <span>{getAddress(address)}</span>
        </PrintViewSection>
      ))}
      {/* Provider locations available in the following location end */}

      <PrintViewDivider orientation="horizontal" />
    </React.Fragment>
  );
};

export default BehavioralHealthProviderPrintView;
